import {
  EVIDENCE_DELIVERY_SOURCES,
  EVIDENCE_HEADER_NAME,
  isViewportRequestOrdinalAllocator,
  readViewportEvidenceDescriptor,
  reserveViewportRequestOrdinal,
} from './viewportEvidenceIdentity';

export const VISIBLE_JPEG_CONTENT_TYPE = 'image/jpeg';
export const VISIBLE_JPEG_PUBLISHER_MODE = 'synchronous-boolean';
export const VISIBLE_JPEG_PUBLISHER_CONTRACT = Object.freeze({
  mode: VISIBLE_JPEG_PUBLISHER_MODE,
  callbackTrust: 'trusted-internal',
  result: 'primitive-boolean',
  asynchronousWork: 'forbidden',
});
export const VISIBLE_JPEG_PUBLISHER_OUTCOMES = Object.freeze({
  PUBLISHED: 'published',
  REJECTED: 'publisher-rejected',
  UNSUPPORTED_RESULT: 'unsupported-publisher-result',
});
export const VISIBLE_JPEG_CANCELLATION_REASONS = Object.freeze({
  CANCELLED: 'cancelled',
  STALE: 'stale',
  SUPERSEDED: 'superseded',
});
export const VISIBLE_JPEG_ERROR_CODES = Object.freeze({
  INVALID_REQUEST: 'invalid-request',
  CROSS_ORIGIN: 'cross-origin',
  ABORTED: 'aborted',
  NETWORK_ERROR: 'network-error',
  FENCE_ERROR: 'fence-error',
  EVIDENCE_TERMINAL: 'evidence-terminal',
  STALE: 'stale',
  SUPERSEDED: 'superseded',
  STATUS: 'status',
  CONTENT_TYPE: 'content-type',
  ECHO: 'echo',
  BODY_ERROR: 'body-error',
  BODY_INCOMPLETE: 'body-incomplete',
  BLOB: 'blob',
  OBJECT_CREATE: 'object-create',
  IMAGE_CREATE: 'image-create',
  DECODE: 'decode',
  PUBLISHER_REJECTED: 'publisher-rejected',
  UNSUPPORTED_PUBLISHER_RESULT: 'unsupported-publisher-result',
});
export const VISIBLE_JPEG_EVENT_TYPES = Object.freeze({
  REQUEST: 'request',
  HEADERS: 'headers',
  BODY: 'body',
  DECODED: 'decoded',
  RETAINED: 'retained',
  RELEASED: 'released',
  PUBLISHED: 'published',
  FAILED: 'failed',
  CANCELLED: 'cancelled',
  STALE: 'stale',
  SUPERSEDED: 'superseded',
  REVOKED: 'revoked',
  REVOKE_FAILED: 'revoke-failed',
});

const VALID_CANCELLATION_REASONS = new Set(Object.values(VISIBLE_JPEG_CANCELLATION_REASONS));
const VALID_ERROR_CODES = new Set(Object.values(VISIBLE_JPEG_ERROR_CODES));
const VALID_EVENT_TYPES = new Set(Object.values(VISIBLE_JPEG_EVENT_TYPES));
const VALID_TERMINAL_OUTCOMES = new Set(['published', 'preloaded', 'failed', 'cancelled', 'stale', 'superseded']);
const SUPPORTED_JPEG_FRAME_MARKERS = new Set([0xc0, 0xc1, 0xc2]);
const callIntrinsicPromiseThen = Function.prototype.call.bind(Promise.prototype.then);
const VALIDATED_PRELOAD_FRAME_IMAGES = new WeakMap();
const CLAIMED_PRELOAD_FRAMES = new WeakSet();
const LOADER_OPTION_KEYS = Object.freeze([
  'benchmarkMode',
  'requestUrl',
  'currentOrigin',
  'evidence',
  'fetchImpl',
  'blobFactory',
  'createObjectURL',
  'revokeObjectURL',
  'createImage',
  'isGenerationCurrent',
  'isAuthorityCurrent',
  'requestAllocator',
  'onEvent',
  'signal',
]);
const REQUIRED_LOADER_OPTION_KEYS = Object.freeze(LOADER_OPTION_KEYS.filter(
  key => key !== 'onEvent' && key !== 'signal'
));

const hasRendererSupportedJpegStructure = bytes => {
  const limit = bytes.length;
  if (limit < 12
      || bytes[0] !== 0xff || bytes[1] !== 0xd8
      || bytes[limit - 2] !== 0xff || bytes[limit - 1] !== 0xd9) return false;

  let offset = 2;
  let frameComponents = 0;
  let sawSupportedFrame = false;
  let sawScan = false;
  while (offset < limit - 2) {
    if (bytes[offset] !== 0xff) return false;
    const markerStart = offset;
    while (offset < limit && bytes[offset] === 0xff) offset += 1;
    if (offset >= limit) return false;
    const marker = bytes[offset];
    offset += 1;
    if (marker === 0x00 || marker === 0xd8 || marker === 0xd9
        || marker === 0x01 || (marker >= 0xd0 && marker <= 0xd7)) return false;
    if (offset + 2 > limit - 2) return false;
    const segmentLength = (bytes[offset] << 8) | bytes[offset + 1];
    if (segmentLength < 2 || offset + segmentLength > limit - 2) return false;

    if (SUPPORTED_JPEG_FRAME_MARKERS.has(marker)) {
      if (segmentLength < 11 || bytes[offset + 2] !== 8) return false;
      const height = (bytes[offset + 3] << 8) | bytes[offset + 4];
      const width = (bytes[offset + 5] << 8) | bytes[offset + 6];
      frameComponents = bytes[offset + 7];
      if (width === 0 || height === 0 || ![1, 3, 4].includes(frameComponents)
          || segmentLength !== 8 + (3 * frameComponents)) return false;
      sawSupportedFrame = true;
    }

    if (marker !== 0xda) {
      offset += segmentLength;
      continue;
    }
    const scanComponents = bytes[offset + 2];
    if (!sawSupportedFrame || scanComponents === 0 || scanComponents > frameComponents
        || segmentLength !== 6 + (2 * scanComponents)) return false;
    sawScan = true;
    offset += segmentLength;

    let foundFollowingMarker = false;
    while (offset < limit) {
      if (bytes[offset] !== 0xff) {
        offset += 1;
        continue;
      }
      const entropyMarkerStart = offset;
      while (offset < limit && bytes[offset] === 0xff) offset += 1;
      if (offset >= limit) return false;
      const entropyMarker = bytes[offset];
      offset += 1;
      if (entropyMarker === 0x00 || (entropyMarker >= 0xd0 && entropyMarker <= 0xd7)) continue;
      if (entropyMarker === 0xd9) return offset === limit && sawSupportedFrame && sawScan;
      offset = entropyMarkerStart;
      foundFollowingMarker = true;
      break;
    }
    if (!foundFollowingMarker) return false;
    if (offset <= markerStart) return false;
  }
  return false;
};

const safeResult = (ok, evidenceOrdinal, requestOrdinal, extra = {}) => Object.freeze({
  ok,
  evidenceOrdinal,
  requestOrdinal,
  publisherMode: VISIBLE_JPEG_PUBLISHER_MODE,
  ...extra,
});

const emptyEvents = () => Object.freeze([]);

const terminalizeBrandedEvidence = (evidence, outcome) => {
  if (!VALID_TERMINAL_OUTCOMES.has(outcome) || !readViewportEvidenceDescriptor(evidence)) return false;
  try { return evidence.terminalize(outcome) === true; } catch (_) { return false; }
};

const invalidOptionsResult = evidence => {
  const descriptor = readViewportEvidenceDescriptor(evidence);
  if (descriptor) terminalizeBrandedEvidence(evidence, 'failed');
  return safeResult(false, descriptor?.evidenceOrdinal || null, null, {
    code: VISIBLE_JPEG_ERROR_CODES.FENCE_ERROR,
    events: emptyEvents,
  });
};

const prepareLoaderOptions = options => {
  try {
    if (!options || typeof options !== 'object' || Array.isArray(options)) {
      return { state: 'disabled', evidence: undefined };
    }
    const benchmarkDescriptor = Object.getOwnPropertyDescriptor(options, 'benchmarkMode');
    if (!benchmarkDescriptor) return { state: 'disabled', evidence: undefined };
    if (!Object.prototype.hasOwnProperty.call(benchmarkDescriptor, 'value')) {
      return { state: 'invalid', evidence: undefined };
    }
    if (benchmarkDescriptor.value !== true) return { state: 'disabled', evidence: undefined };

    const keys = Reflect.ownKeys(options);
    let invalid = keys.some(key => typeof key !== 'string' || !LOADER_OPTION_KEYS.includes(key))
      || REQUIRED_LOADER_OPTION_KEYS.some(key => !keys.includes(key));
    const snapshot = Object.create(null);
    for (const key of keys) {
      if (typeof key !== 'string' || !LOADER_OPTION_KEYS.includes(key)) continue;
      const descriptor = key === 'benchmarkMode'
        ? benchmarkDescriptor
        : Object.getOwnPropertyDescriptor(options, key);
      if (!descriptor || !Object.prototype.hasOwnProperty.call(descriptor, 'value')) {
        invalid = true;
        continue;
      }
      snapshot[key] = descriptor.value;
    }
    if (invalid || snapshot.benchmarkMode !== true) {
      return { state: 'invalid', evidence: snapshot.evidence };
    }
    return { state: 'enabled', snapshot: Object.freeze(snapshot), evidence: snapshot.evidence };
  } catch (_) {
    return { state: 'invalid', evidence: undefined };
  }
};

export const loadVisibleJpegNetworkFrame = options => {
  const preparation = prepareLoaderOptions(options);
  if (preparation.state === 'disabled') return null;
  if (preparation.state === 'invalid') return Promise.resolve(invalidOptionsResult(preparation.evidence));
  return loadEnabledVisibleJpegNetworkFrame(preparation.snapshot);
};

const loadEnabledVisibleJpegNetworkFrame = async snapshot => {
  const {
    requestUrl,
    currentOrigin,
    evidence,
    fetchImpl,
    blobFactory,
    createObjectURL,
    revokeObjectURL,
    createImage,
    isGenerationCurrent,
    isAuthorityCurrent,
    requestAllocator,
    onEvent,
    signal,
  } = snapshot;
  const descriptorFor = consumerEvidence => readViewportEvidenceDescriptor(consumerEvidence);
  const terminalizeConsumerEvidence = (consumerEvidence, outcome) => {
    if (!VALID_TERMINAL_OUTCOMES.has(outcome) || !descriptorFor(consumerEvidence)) return false;
    try { return consumerEvidence.terminalize(outcome) === true; } catch (_) { return false; }
  };
  const terminalizeEvidence = outcome => terminalizeConsumerEvidence(evidence, outcome);
  const consumerEvidenceStatus = consumerEvidence => {
    if (!descriptorFor(consumerEvidence)) return null;
    try {
      const status = consumerEvidence.status();
      if (status && typeof status.terminalized === 'boolean') return status;
    } catch (_) {}
    return null;
  };
  const evidenceStatus = () => consumerEvidenceStatus(evidence);
  const descriptor = descriptorFor(evidence);
  if (!descriptor || descriptor.deliverySource !== EVIDENCE_DELIVERY_SOURCES.NETWORK) {
    if (descriptor) terminalizeEvidence('failed');
    return safeResult(false, descriptor?.evidenceOrdinal || null, null, {
      code: VISIBLE_JPEG_ERROR_CODES.FENCE_ERROR,
      events: emptyEvents,
    });
  }
  if (typeof fetchImpl !== 'function'
      || typeof blobFactory !== 'function'
      || typeof createObjectURL !== 'function'
      || typeof revokeObjectURL !== 'function'
      || typeof createImage !== 'function'
      || typeof isGenerationCurrent !== 'function'
      || typeof isAuthorityCurrent !== 'function'
      || !isViewportRequestOrdinalAllocator(requestAllocator)
      || typeof requestUrl !== 'string'
      || typeof currentOrigin !== 'string'
      || (onEvent !== undefined && typeof onEvent !== 'function')
      || (signal !== undefined && (signal === null
        || (typeof signal !== 'object' && typeof signal !== 'function')))) {
    terminalizeEvidence('failed');
    return safeResult(false, descriptor.evidenceOrdinal, null, {
      code: VISIBLE_JPEG_ERROR_CODES.FENCE_ERROR,
      events: emptyEvents,
    });
  }

  let requestOrdinal;
  try { requestOrdinal = reserveViewportRequestOrdinal(requestAllocator); } catch (_) {
    terminalizeEvidence('failed');
    return safeResult(false, descriptor.evidenceOrdinal, null, {
      code: VISIBLE_JPEG_ERROR_CODES.FENCE_ERROR,
      events: emptyEvents,
    });
  }
  const evidenceOrdinal = descriptor.evidenceOrdinal;
  const eventLog = [];
  const emit = (type, eventEvidenceOrdinal = evidenceOrdinal) => {
    if (!VALID_EVENT_TYPES.has(type)) throw new Error('Network loader event type is invalid');
    const event = Object.freeze({ evidenceOrdinal: eventEvidenceOrdinal, requestOrdinal, type });
    eventLog.push(event);
    try { onEvent?.(event); } catch (_) {}
  };
  const events = () => Object.freeze([...eventLog]);

  let objectUrl = null;
  let revoked = false;
  let revocationPending = false;
  let revocationRetryCapability = null;
  const attemptRevoke = () => {
    if (revoked || objectUrl === null) return false;
    try {
      revokeObjectURL(objectUrl);
      revoked = true;
      revocationPending = false;
      revocationRetryCapability = null;
      emit(VISIBLE_JPEG_EVENT_TYPES.REVOKED);
      return true;
    } catch (_) {
      revocationPending = true;
      emit(VISIBLE_JPEG_EVENT_TYPES.REVOKE_FAILED);
      return false;
    }
  };
  const terminalEventFor = outcome => ({
    failed: VISIBLE_JPEG_EVENT_TYPES.FAILED,
    cancelled: VISIBLE_JPEG_EVENT_TYPES.CANCELLED,
    stale: VISIBLE_JPEG_EVENT_TYPES.STALE,
    superseded: VISIBLE_JPEG_EVENT_TYPES.SUPERSEDED,
  })[outcome] || VISIBLE_JPEG_EVENT_TYPES.FAILED;
  const fail = (code, outcome = 'failed') => {
    if (!VALID_ERROR_CODES.has(code)) throw new Error('Network loader error code is invalid');
    const status = evidenceStatus();
    if (!status?.terminalized) terminalizeEvidence(outcome);
    let cleanup;
    if (objectUrl !== null && !revoked && !attemptRevoke() && revocationPending) {
      // This capability intentionally closes over the private object URL but
      // never exposes it, exception text, or any caller-controlled value.
      cleanup = Object.freeze({
        retry: () => (revocationPending && !revoked ? attemptRevoke() : false),
        snapshot: () => Object.freeze({ revoked, revocationPending }),
      });
    }
    emit(terminalEventFor(evidenceStatus()?.outcome || outcome));
    return safeResult(false, evidenceOrdinal, requestOrdinal, cleanup ? { code, events, cleanup } : { code, events });
  };
  const inspectSignal = candidateSignal => {
    try { return { readable: true, aborted: candidateSignal?.aborted === true }; } catch (_) {
      return { readable: false, aborted: true };
    }
  };
  const failForSignal = defaultCode => {
    const inspection = inspectSignal(signal);
    if (!inspection.readable) return fail(VISIBLE_JPEG_ERROR_CODES.FENCE_ERROR, 'failed');
    return inspection.aborted
      ? fail(VISIBLE_JPEG_ERROR_CODES.ABORTED, 'cancelled')
      : fail(defaultCode, 'failed');
  };
  const inspectCurrentness = (consumer = {}) => {
    const consumerEvidence = consumer.evidence || evidence;
    const consumerSignal = Object.prototype.hasOwnProperty.call(consumer, 'signal') ? consumer.signal : signal;
    const generationFence = consumer.isGenerationCurrent || isGenerationCurrent;
    const authorityFence = consumer.isAuthorityCurrent || isAuthorityCurrent;
    const signalInspection = inspectSignal(consumerSignal);
    if (!signalInspection.readable) {
      return { ok: false, code: VISIBLE_JPEG_ERROR_CODES.FENCE_ERROR, outcome: 'failed' };
    }
    if (signalInspection.aborted) {
      return { ok: false, code: VISIBLE_JPEG_ERROR_CODES.ABORTED, outcome: 'cancelled' };
    }
    const statusBefore = consumerEvidenceStatus(consumerEvidence);
    if (!statusBefore) {
      return { ok: false, code: VISIBLE_JPEG_ERROR_CODES.FENCE_ERROR, outcome: 'failed' };
    }
    if (statusBefore.terminalized) {
      return { ok: false, code: VISIBLE_JPEG_ERROR_CODES.EVIDENCE_TERMINAL, outcome: statusBefore.outcome };
    }
    let generationCurrent;
    let authorityCurrent;
    try {
      generationCurrent = generationFence() === true;
      authorityCurrent = authorityFence() === true;
    } catch (_) {
      return { ok: false, code: VISIBLE_JPEG_ERROR_CODES.FENCE_ERROR, outcome: 'failed' };
    }
    let authorized;
    try {
      authorized = consumerEvidence.authorizePublication({ generationCurrent, authorityCurrent }) === true;
    } catch (_) {
      return { ok: false, code: VISIBLE_JPEG_ERROR_CODES.FENCE_ERROR, outcome: 'failed' };
    }
    if (authorized) return { ok: true };
    return generationCurrent
      ? { ok: false, code: VISIBLE_JPEG_ERROR_CODES.SUPERSEDED, outcome: 'superseded' }
      : { ok: false, code: VISIBLE_JPEG_ERROR_CODES.STALE, outcome: 'stale' };
  };
  const rejectIfNotCurrent = () => {
    const inspection = inspectCurrentness();
    return inspection.ok ? null : fail(inspection.code, inspection.outcome);
  };

  let resolvedOrigin;
  let absoluteRequest;
  try {
    resolvedOrigin = new URL(currentOrigin).origin;
    absoluteRequest = new URL(requestUrl, resolvedOrigin);
  } catch (_) {
    return fail(VISIBLE_JPEG_ERROR_CODES.INVALID_REQUEST);
  }
  if (!['http:', 'https:'].includes(absoluteRequest.protocol)
      || absoluteRequest.origin !== resolvedOrigin
      || absoluteRequest.username || absoluteRequest.password) {
    return fail(VISIBLE_JPEG_ERROR_CODES.CROSS_ORIGIN);
  }

  const rejectedBeforeRequest = rejectIfNotCurrent();
  if (rejectedBeforeRequest) return rejectedBeforeRequest;

  let requestHeader;
  try { requestHeader = evidence.requestHeader(); } catch (_) {
    return fail(VISIBLE_JPEG_ERROR_CODES.FENCE_ERROR);
  }
  emit(VISIBLE_JPEG_EVENT_TYPES.REQUEST);
  let response;
  try {
    response = await fetchImpl(absoluteRequest.href, {
      method: 'GET',
      mode: 'same-origin',
      credentials: 'include',
      headers: { [EVIDENCE_HEADER_NAME]: requestHeader.value },
      signal,
    });
  } catch (_) {
    return failForSignal(VISIBLE_JPEG_ERROR_CODES.NETWORK_ERROR);
  }

  const rejectedAfterHeaders = rejectIfNotCurrent();
  if (rejectedAfterHeaders) return rejectedAfterHeaders;
  let responseStatus;
  let responseHeaders;
  let getResponseHeader;
  let contentType;
  let responseEcho;
  try {
    responseStatus = response?.status;
    responseHeaders = response?.headers;
    getResponseHeader = responseHeaders?.get;
    if (typeof getResponseHeader !== 'function') throw new Error('invalid response headers');
    contentType = Reflect.apply(getResponseHeader, responseHeaders, ['content-type']);
    responseEcho = Reflect.apply(getResponseHeader, responseHeaders, [EVIDENCE_HEADER_NAME]);
  } catch (_) {
    return fail(VISIBLE_JPEG_ERROR_CODES.FENCE_ERROR);
  }
  if (!(Number.isInteger(responseStatus) && responseStatus >= 200 && responseStatus <= 299)) {
    return fail(VISIBLE_JPEG_ERROR_CODES.STATUS);
  }
  if (contentType !== VISIBLE_JPEG_CONTENT_TYPE) {
    return fail(VISIBLE_JPEG_ERROR_CODES.CONTENT_TYPE);
  }
  let echoMatches = false;
  try { echoMatches = evidence.matchesResponseEcho(responseEcho) === true; } catch (_) {
    return fail(VISIBLE_JPEG_ERROR_CODES.FENCE_ERROR);
  }
  if (!echoMatches) {
    return fail(VISIBLE_JPEG_ERROR_CODES.ECHO);
  }
  emit(VISIBLE_JPEG_EVENT_TYPES.HEADERS);

  let ownedBody;
  try {
    const readBody = response?.arrayBuffer;
    if (typeof readBody !== 'function') throw new Error('invalid response body');
    const suppliedBody = await Reflect.apply(readBody, response, []);
    if (!(suppliedBody instanceof ArrayBuffer)) throw new Error('invalid response body');
    // Ownership is established in the same containment block as the provider
    // await, before any fence/header/event callback can detach or mutate the
    // provider's buffer. Every later byte/length/Blob operation uses this copy.
    const suppliedBytes = new Uint8Array(suppliedBody);
    ownedBody = new Uint8Array(suppliedBytes.length);
    ownedBody.set(suppliedBytes);
  } catch (_) {
    return failForSignal(VISIBLE_JPEG_ERROR_CODES.BODY_ERROR);
  }
  const rejectedAfterBody = rejectIfNotCurrent();
  if (rejectedAfterBody) return rejectedAfterBody;
  const ownedLength = ownedBody.byteLength;
  if (ownedLength <= 0) return fail(VISIBLE_JPEG_ERROR_CODES.BODY_INCOMPLETE);
  let declaredLength;
  try { declaredLength = Reflect.apply(getResponseHeader, responseHeaders, ['content-length']); } catch (_) {
    return fail(VISIBLE_JPEG_ERROR_CODES.FENCE_ERROR);
  }
  if (declaredLength !== null) {
    if (typeof declaredLength !== 'string') return fail(VISIBLE_JPEG_ERROR_CODES.BODY_INCOMPLETE);
    const numericDeclaredLength = /^[1-9][0-9]*$/.test(declaredLength)
      ? Number(declaredLength)
      : Number.NaN;
    if (!Number.isSafeInteger(numericDeclaredLength)
        || numericDeclaredLength !== ownedLength) return fail(VISIBLE_JPEG_ERROR_CODES.BODY_INCOMPLETE);
  }
  if (!hasRendererSupportedJpegStructure(ownedBody)) return fail(VISIBLE_JPEG_ERROR_CODES.BODY_INCOMPLETE);
  emit(VISIBLE_JPEG_EVENT_TYPES.BODY);

  let image;
  try {
    const blob = blobFactory([ownedBody], { type: VISIBLE_JPEG_CONTENT_TYPE });
    if (!blob || blob.size !== ownedLength || blob.type !== VISIBLE_JPEG_CONTENT_TYPE) {
      return fail(VISIBLE_JPEG_ERROR_CODES.BLOB);
    }
    objectUrl = createObjectURL(blob);
    if (typeof objectUrl !== 'string' || objectUrl.length === 0) return fail(VISIBLE_JPEG_ERROR_CODES.OBJECT_CREATE);
    const rejectedBeforeDecode = rejectIfNotCurrent();
    if (rejectedBeforeDecode) return rejectedBeforeDecode;
    image = createImage();
    const decodeImage = image?.decode;
    if (!image || typeof decodeImage !== 'function') return fail(VISIBLE_JPEG_ERROR_CODES.IMAGE_CREATE);
    image.src = objectUrl;
    await Reflect.apply(decodeImage, image, []);
  } catch (_) {
    return failForSignal(VISIBLE_JPEG_ERROR_CODES.DECODE);
  }
  const rejectedAfterDecode = rejectIfNotCurrent();
  if (rejectedAfterDecode) return rejectedAfterDecode;
  let decodedImageIsUsable = false;
  try {
    decodedImageIsUsable = image.complete === true && image.naturalWidth > 0 && image.naturalHeight > 0;
  } catch (_) {
    return fail(VISIBLE_JPEG_ERROR_CODES.DECODE);
  }
  if (!decodedImageIsUsable) {
    return fail(VISIBLE_JPEG_ERROR_CODES.DECODE);
  }
  emit(VISIBLE_JPEG_EVENT_TYPES.DECODED);
  const rejectedAfterDecodedEvent = rejectIfNotCurrent();
  if (rejectedAfterDecodedEvent) return rejectedAfterDecodedEvent;

  const consumers = new Set();
  const makeCapability = (consumerOptions, isRoot = false) => {
    let consumerEvidence;
    let generationFence;
    let authorityFence;
    let consumerSignal;
    try {
      consumerEvidence = consumerOptions.evidence;
      generationFence = consumerOptions.isGenerationCurrent;
      authorityFence = consumerOptions.isAuthorityCurrent;
      consumerSignal = consumerOptions.signal;
    } catch (_) {
      return { capability: null, evidence: consumerEvidence };
    }
    const consumerDescriptor = descriptorFor(consumerEvidence);
    const expectedSource = isRoot
      ? EVIDENCE_DELIVERY_SOURCES.NETWORK
      : EVIDENCE_DELIVERY_SOURCES.DECODED_CACHE;
    if (!consumerDescriptor || consumerDescriptor.deliverySource !== expectedSource
        || typeof generationFence !== 'function' || typeof authorityFence !== 'function') {
      return { capability: null, evidence: consumerEvidence };
    }
    return {
      evidence: consumerEvidence,
      capability: {
        evidence: consumerEvidence,
        isGenerationCurrent: generationFence,
        isAuthorityCurrent: authorityFence,
        signal: consumerSignal,
        descriptor: consumerDescriptor,
        isRoot,
        released: false,
        publishUsed: false,
        published: false,
        publicationOutcome: null,
        unusable: false,
        publicationStarted: false,
      },
    };
  };
  const rootConstruction = makeCapability({
    evidence,
    isGenerationCurrent,
    isAuthorityCurrent,
    signal,
  }, true);
  const rootCapability = rootConstruction.capability;
  if (!rootCapability) {
    terminalizeConsumerEvidence(rootConstruction.evidence, 'failed');
    return fail(VISIBLE_JPEG_ERROR_CODES.FENCE_ERROR);
  }
  const referenceCount = () => (rootCapability.released ? 0 : 1) + consumers.size;
  const finalizeReleasedOwnership = capability => {
    if (referenceCount() !== 0) return true;
    if (attemptRevoke()) return true;
    if (revocationPending) revocationRetryCapability = capability;
    return false;
  };
  const dropCapability = capability => {
    if (capability.released) return false;
    capability.released = true;
    if (!capability.isRoot) consumers.delete(capability);
    return true;
  };
  const closeForFailure = (outcome, capability) => {
    capability.unusable = true;
    dropCapability(capability);
    const status = consumerEvidenceStatus(capability.evidence);
    if (!status?.terminalized) terminalizeConsumerEvidence(capability.evidence, outcome);
    const finalStatus = consumerEvidenceStatus(capability.evidence);
    emit(terminalEventFor(finalStatus?.outcome || outcome), capability.descriptor.evidenceOrdinal);
    if (referenceCount() === 0) finalizeReleasedOwnership(capability);
    return false;
  };
  const publishCapability = (publisher, capability) => {
    if (capability.publicationStarted || capability.published || capability.unusable || revoked
        || capability.released || capability.publishUsed) return false;
    capability.publishUsed = true;
    if (typeof publisher !== 'function') {
      capability.publicationOutcome = VISIBLE_JPEG_PUBLISHER_OUTCOMES.UNSUPPORTED_RESULT;
      return closeForFailure('failed', capability);
    }

    const before = inspectCurrentness(capability);
    if (!before.ok) return closeForFailure(before.outcome, capability);
    capability.publicationStarted = true;
    let result;
    let publisherThrew = false;
    try { result = publisher({ image, objectUrl }); } catch (_) { publisherThrew = true; }
    if (publisherThrew) {
      capability.publicationOutcome = VISIBLE_JPEG_PUBLISHER_OUTCOMES.REJECTED;
      capability.publicationStarted = false;
      return closeForFailure('failed', capability);
    }
    if (typeof result !== 'boolean') {
      if (result !== null && (typeof result === 'object' || typeof result === 'function')) {
        try {
          callIntrinsicPromiseThen(result, undefined, () => undefined);
        } catch (_) {
          // Best effort only. Promise.prototype.then and PromiseResolve both
          // consult constructor/species. An already-rejected genuine Promise
          // with a non-configurable throwing constructor cannot be assimilated
          // by any ECMAScript public API. Such caller-created asynchronous work
          // violates VISIBLE_JPEG_PUBLISHER_CONTRACT and remains caller-owned.
        }
      }
      capability.publicationOutcome = VISIBLE_JPEG_PUBLISHER_OUTCOMES.UNSUPPORTED_RESULT;
      capability.publicationStarted = false;
      return closeForFailure('failed', capability);
    }
    if (result === false) {
      capability.publicationOutcome = VISIBLE_JPEG_PUBLISHER_OUTCOMES.REJECTED;
      capability.publicationStarted = false;
      return closeForFailure('failed', capability);
    }

    // The trusted synchronous callback remains a reentrancy point. Recheck
    // this consumer's fences and evidence state before claiming success.
    const after = inspectCurrentness(capability);
    if (!after.ok) {
      capability.publicationOutcome = VISIBLE_JPEG_PUBLISHER_OUTCOMES.REJECTED;
      capability.publicationStarted = false;
      return closeForFailure(after.outcome, capability);
    }
    const terminalized = terminalizeConsumerEvidence(capability.evidence, 'published');
    if (!terminalized) {
      capability.publicationOutcome = VISIBLE_JPEG_PUBLISHER_OUTCOMES.REJECTED;
      capability.publicationStarted = false;
      return closeForFailure(consumerEvidenceStatus(capability.evidence)?.outcome || 'failed', capability);
    }
    capability.published = true;
    capability.publicationOutcome = VISIBLE_JPEG_PUBLISHER_OUTCOMES.PUBLISHED;
    emit(VISIBLE_JPEG_EVENT_TYPES.PUBLISHED, capability.descriptor.evidenceOrdinal);
    capability.publicationStarted = false;
    return true;
  };
  const snapshotFor = capability => Object.freeze({
    evidenceOrdinal: capability.descriptor.evidenceOrdinal,
    requestOrdinal,
    referenceCount: referenceCount(),
    published: capability.published,
    publicationOutcome: capability.publicationOutcome,
    revoked,
    revocationPending,
  });
  const releaseCapability = capability => {
    if (capability.publicationStarted) return false;
    if (capability.released) {
      if (revocationPending && referenceCount() === 0 && revocationRetryCapability === capability) {
        return attemptRevoke();
      }
      return false;
    }
    dropCapability(capability);
    emit(VISIBLE_JPEG_EVENT_TYPES.RELEASED, capability.descriptor.evidenceOrdinal);
    const status = consumerEvidenceStatus(capability.evidence);
    if (!status?.terminalized) {
      terminalizeConsumerEvidence(capability.evidence, 'cancelled');
      emit(VISIBLE_JPEG_EVENT_TYPES.CANCELLED, capability.descriptor.evidenceOrdinal);
    }
    return finalizeReleasedOwnership(capability);
  };
  const createConsumerHandle = consumerOptions => {
    if (revoked || rootCapability.released || consumerOptions === undefined) return false;
    const construction = makeCapability(consumerOptions, false);
    const capability = construction.capability;
    if (!capability) {
      terminalizeConsumerEvidence(construction.evidence, 'failed');
      return false;
    }
    consumers.add(capability);
    emit(VISIBLE_JPEG_EVENT_TYPES.RETAINED, capability.descriptor.evidenceOrdinal);
    return Object.freeze({
      publish: publisher => publishCapability(publisher, capability),
      release: () => releaseCapability(capability),
      descriptor: () => capability.descriptor,
      snapshot: () => snapshotFor(capability),
    });
  };
  const frame = Object.freeze({
    publish: publisher => publishCapability(publisher, rootCapability),
    acquire: createConsumerHandle,
    retain: createConsumerHandle,
    release: () => releaseCapability(rootCapability),
    cancel(reason = VISIBLE_JPEG_CANCELLATION_REASONS.CANCELLED) {
      if (!VALID_CANCELLATION_REASONS.has(reason)) throw new Error('Frame cancellation reason is invalid');
      if (rootCapability.publicationStarted || rootCapability.published
          || rootCapability.unusable || rootCapability.released) return false;
      rootCapability.unusable = true;
      dropCapability(rootCapability);
      terminalizeConsumerEvidence(rootCapability.evidence, reason);
      emit(terminalEventFor(reason), rootCapability.descriptor.evidenceOrdinal);
      if (referenceCount() === 0) finalizeReleasedOwnership(rootCapability);
      return true;
    },
    descriptor: () => rootCapability.descriptor,
    snapshot: () => snapshotFor(rootCapability),
  });

  VALIDATED_PRELOAD_FRAME_IMAGES.set(frame, image);
  return safeResult(true, evidenceOrdinal, requestOrdinal, { frame, events });
};

export const claimVisibleJpegValidatedPreloadRoot = frame => {
  try {
    const image = VALIDATED_PRELOAD_FRAME_IMAGES.get(frame);
    if (!image || CLAIMED_PRELOAD_FRAMES.has(frame)) return null;
    CLAIMED_PRELOAD_FRAMES.add(frame);
    return Object.freeze({ frame, image });
  } catch (_) {
    return null;
  }
};
